/**
 * @fileoverview Custom React hooks for Hydraulic Balancing Simulation
 * 
 * Provides state management and simulation logic hooks including:
 * - Building state management
 * - Simulation animation control
 * - Static value calculations (losses, flow rates)
 * - Room selection and manipulation
 * - Import/Export functionality
 * 
 * Architecture follows similar principles to HeatPumpSimulation:
 * - Separate static calculations from animation updates
 * - Recalculate derived values when parameters change
 * - Animation only updates temperatures
 * 
 * @module HydraulicBalancing/hooks
 * @author HeatCraftHP Team
 * @version 2.0.0
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { message } from 'antd';

import {
  SIMULATION_CONFIG,
  HEAT_PUMP_CONFIG,
  OUTDOOR_CONFIG,
  PRESSURE_CONFIG,
  VALVE_CONFIG,
  RADIATOR_CONFIG,
  PIPING_CONFIG,
  BUILDING_CONFIG,
  createDefaultRoom,
  createInitialBuilding,
} from './constants';

import {
  simulateRoomStep,
  getNeighborTemperatures,
  calculateHeatPumpCOP,
  calculateMixedReturnTemperature,
  calculateBranchFlow,
  calculateRadiatorPerformance,
  calculateTotalRoomLosses,
  canPlaceRoom,
  canDeleteRoom,
  massFlowToLPM,
} from './utils';

import { WATER_PROPERTIES } from './constants';

// #region SIMULATION STATE HOOK
// ============================================================================
// SIMULATION STATE HOOK
// ============================================================================

/**
 * Hook for managing the complete simulation state
 * 
 * Follows the same architectural principles as HeatPumpSimulation:
 * 1. calculateStaticValues - Computes losses, flow rates, heat output (called on parameter change)
 * 2. runSimulationStep - Updates temperatures during animation only
 * 3. Separate useEffects for static recalculation vs animation timer
 * 
 * @returns {Object} Simulation state and control functions
 */
export const useSimulation = () => {
  // -------------------------------------------------------------------------
  // System Parameters State
  // -------------------------------------------------------------------------
  
  /** Outdoor temperature [°C] */
  const [tOutdoor, setTOutdoor] = useState(OUTDOOR_CONFIG.DEFAULT_OUTDOOR_TEMP);
  
  /** Supply water temperature [°C] */
  const [tSupply, setTSupply] = useState(HEAT_PUMP_CONFIG.DEFAULT_SUPPLY_TEMP);
  
  /** Room-to-room U-value [W/(m²·K)] */
  const [uRoomToRoom, setURoomToRoom] = useState(BUILDING_CONFIG.DEFAULT_U_ROOM_TO_ROOM);
  
  /** System differential pressure [bar] */
  const [systemPressure, setSystemPressure] = useState(PRESSURE_CONFIG.DEFAULT_DP);
  
  /** Maximum valve Kv [m³/h] */
  const [kvMax, setKvMax] = useState(VALVE_CONFIG.DEFAULT_KV_MAX);
  
  /** Radiator heat coefficient per section [W/K^n] */
  const [kRad, setKRad] = useState(RADIATOR_CONFIG.DEFAULT_K_RAD);
  
  /** Pipe length per branch [m] */
  const [pipeLength, setPipeLength] = useState(PIPING_CONFIG.DEFAULT_PIPE_LENGTH);
  
  /** Pipe diameter [mm] */
  const [pipeDiameter, setPipeDiameter] = useState(PIPING_CONFIG.DEFAULT_PIPE_DIAMETER);
  
  /** Air change rate [ACH] */
  const [airChangeRate, setAirChangeRate] = useState(0.5);

  // -------------------------------------------------------------------------
  // Building State
  // -------------------------------------------------------------------------
  
  /** 2D array of room states [floor][position] */
  const [rooms, setRooms] = useState(createInitialBuilding);
  
  /** Currently selected room indices [floor, position] */
  const [selectedRoom, setSelectedRoom] = useState([0, 0]);
  
  // -------------------------------------------------------------------------
  // Computed Values State (updated by calculateStaticValues)
  // -------------------------------------------------------------------------
  
  /** Total envelope heat loss for building [W] */
  const [totalEnvelopeLoss, setTotalEnvelopeLoss] = useState(0);
  
  /** Total ventilation heat loss for building [W] */
  const [totalVentilationLoss, setTotalVentilationLoss] = useState(0);
  
  /** Total heat output from all radiators [W] */
  const [totalHeatOutput, setTotalHeatOutput] = useState(0);
  
  /** Total mass flow rate [kg/s] */
  const [totalMassFlow, setTotalMassFlow] = useState(0);
  
  // -------------------------------------------------------------------------
  // Simulation Control State
  // -------------------------------------------------------------------------
  
  /** Whether simulation is currently running */
  const [isRunning, setIsRunning] = useState(false);
  
  /** Timer reference for cleanup */
  const timerRef = useRef(null);

  // -------------------------------------------------------------------------
  // Derived Values
  // -------------------------------------------------------------------------
  
  /**
   * Calculates the current heat pump COP
   */
  const cop = calculateHeatPumpCOP(tSupply, tOutdoor);
  
  /**
   * Calculates mixed return temperature from all circuits
   */
  const getMixedReturnTemp = useCallback(() => {
    const circuits = rooms
      .flat()
      .filter(Boolean)
      .map(room => ({
        massFlow: room.mFlow[room.mFlow.length - 1] || 0,
        temperature: room.Tout || tSupply,
      }));
    
    return calculateMixedReturnTemperature(circuits);
  }, [rooms, tSupply]);
  
  /**
   * Gets combined thermostat valve openings for fan animation
   */
  const getCombinedValveOpening = useCallback(() => {
    return rooms
      .flat()
      .filter(Boolean)
      .reduce((sum, room) => sum + room.kThermostat, 0);
  }, [rooms]);

  // -------------------------------------------------------------------------
  // Heat Loss Calculation (similar to HeatPumpSimulation.calculateHeatLosses)
  // -------------------------------------------------------------------------
  
  /**
   * Calculates heat losses for a single room
   * Similar to calculateHeatLosses in HeatPumpSimulation hooks
   * 
   * @param {Object} room - Room state object
   * @param {Object} neighbors - Neighbor temperatures
   * @returns {Object} { envelopeLoss, ventilationLoss, interRoomLoss, totalLoss }
   */
  const calculateRoomHeatLosses = useCallback((room, neighbors) => {
    const tRoom = room.Tc[room.Tc.length - 1];
    
    const losses = calculateTotalRoomLosses({
      tRoom,
      tOutside: tOutdoor,
      uOutdoor: room.Uout,
      outdoorArea: room.area || BUILDING_CONFIG.WALL_AREA,
      neighbors,
      airChangeRate,
    });
    
    return {
      envelopeLoss: losses.transmissionLoss,
      ventilationLoss: losses.ventilationLoss,
      interRoomLoss: losses.interRoomLoss,
      totalLoss: losses.totalLoss,
    };
  }, [tOutdoor, airChangeRate]);

  // -------------------------------------------------------------------------
  // Static Values Calculation (similar to HeatPumpSimulation.calculateStaticValues)
  // -------------------------------------------------------------------------
  
  /**
   * Calculates static values (losses, flow rates, heat output) for all rooms
   * Called whenever system parameters change, regardless of animation state
   * 
   * This follows the same principle as calculateStaticValues in HeatPumpSimulation:
   * - Calculate volumetric/mass flow rates
   * - Calculate radiator heat transfer
   * - Calculate envelope and ventilation losses
   * - Update derived state values
   */
  const calculateStaticValues = useCallback(() => {
    let buildingEnvelopeLoss = 0;
    let buildingVentilationLoss = 0;
    let buildingHeatOutput = 0;
    let buildingMassFlow = 0;
    
    // Calculate values for each room
    setRooms(prevRooms => {
      const updatedRooms = prevRooms.map((floor, floorIndex) =>
        floor.map((room, colIndex) => {
          if (!room) return room;
          
          const tRoom = room.Tc[room.Tc.length - 1];
          const neighbors = getNeighborTemperatures(prevRooms, floorIndex, colIndex);
          
          // Calculate volumetric flow rate through this branch using CURRENT thermostat opening
          // Similar to calculateVolumetricFlow in HeatPumpSimulation
          const volumeFlow = calculateBranchFlow({
            floorIndex,
            columnIndex: colIndex,
            systemPressure,
            pipeLength,
            pipeDiameter,
            kvMax,
            presetOpening: room.valve,
            thermostatOpening: room.kThermostat,
          });
          
          const massFlow = volumeFlow * WATER_PROPERTIES.DENSITY;
          
          // Calculate radiator heat transfer
          // Similar to calculateRadiatorHeatTransfer in HeatPumpSimulation
          const { Treturn, heatOutput } = calculateRadiatorPerformance({
            Tsupply: tSupply,
            Troom: tRoom,
            massFlow,
            nSections: room.nRad,
            kRad,
          });
          
          // Calculate heat losses
          // Similar to calculateHeatLosses in HeatPumpSimulation
          const losses = calculateRoomHeatLosses(room, neighbors);
          
          // Accumulate building totals
          buildingEnvelopeLoss += losses.envelopeLoss;
          buildingVentilationLoss += losses.ventilationLoss;
          buildingHeatOutput += heatOutput;
          buildingMassFlow += massFlow;
          
          // Update room with calculated static values (not temperature or thermostat)
          return {
            ...room,
            Tout: Treturn,
            // Update last values in history arrays for display
            mFlow: [...room.mFlow.slice(0, -1), massFlow],
            qrad: [...room.qrad.slice(0, -1), heatOutput],
            // Store calculated losses for overlays
            envelopeLoss: losses.envelopeLoss,
            ventilationLoss: losses.ventilationLoss,
          };
        })
      );
      
      return updatedRooms;
    });
    
    // Update building-wide computed values
    setTotalEnvelopeLoss(buildingEnvelopeLoss);
    setTotalVentilationLoss(buildingVentilationLoss);
    setTotalHeatOutput(buildingHeatOutput);
    setTotalMassFlow(buildingMassFlow);
  }, [
    systemPressure,
    pipeLength,
    pipeDiameter,
    kvMax,
    tSupply,
    kRad,
    calculateRoomHeatLosses,
  ]);

  // -------------------------------------------------------------------------
  // Simulation Step Function (temperature updates only - for animation)
  // -------------------------------------------------------------------------
  
  /**
   * Executes one simulation step for all rooms
   * Only updates temperatures - similar to simulationStep in HeatPumpSimulation
   * Static values are already calculated by calculateStaticValues
   */
  const runSimulationStep = useCallback(() => {
    setRooms(prevRooms => {
      const newRooms = prevRooms.map((floor, floorIndex) => 
        floor.map((room, colIndex) => {
          if (!room) return room;
          
          const neighbors = getNeighborTemperatures(prevRooms, floorIndex, colIndex);
          
          return simulateRoomStep({
            room,
            tOutdoor,
            tSupply,
            kRad,
            neighbors,
            systemPressure,
            floorIndex,
            columnIndex: colIndex,
            pipeLength,
            pipeDiameter,
            kvMax,
            airChangeRate,
          });
        })
      );
      
      return newRooms;
    });
  }, [tOutdoor, tSupply, kRad, systemPressure, pipeLength, pipeDiameter, kvMax, airChangeRate]);

  // -------------------------------------------------------------------------
  // Static Values Recalculation Effect
  // (Similar to HeatPumpSimulation useEffect for calculateStaticValues)
  // -------------------------------------------------------------------------
  
  /**
   * Recalculate static values whenever system parameters change
   * This runs regardless of animation state, just like in HeatPumpSimulation
   */
  useEffect(() => {
    calculateStaticValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // System parameters that affect calculations
    tOutdoor,
    tSupply,
    systemPressure,
    pipeLength,
    pipeDiameter,
    kvMax,
    kRad,
    airChangeRate,
    uRoomToRoom,
    // Note: rooms is intentionally excluded to prevent infinite loop
    // Room changes are handled separately
  ]);

  // -------------------------------------------------------------------------
  // Animation Control Effect
  // (Similar to HeatPumpSimulation useEffect for animation timer)
  // -------------------------------------------------------------------------
  
  /**
   * Starts or stops the simulation animation timer
   */
  useEffect(() => {
    if (isRunning) {
      timerRef.current = setInterval(runSimulationStep, SIMULATION_CONFIG.TIME_STEP_MS);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isRunning, runSimulationStep]);

  // -------------------------------------------------------------------------
  // Room Manipulation Functions
  // -------------------------------------------------------------------------
  
  /**
   * Updates a property of the selected room
   * @param {string} property - Property name to update
   * @param {any} value - New value
   */
  const updateSelectedRoom = useCallback((property, value) => {
    const [floor, col] = selectedRoom;
    
    setRooms(prevRooms => {
      const newRooms = [...prevRooms];
      newRooms[floor] = [...newRooms[floor]];
      newRooms[floor][col] = {
        ...newRooms[floor][col],
        [property]: value,
      };
      return newRooms;
    });
    
    // Recalculate static values after room property change
    // Use setTimeout to allow state update to complete first
    setTimeout(() => calculateStaticValues(), 0);
  }, [selectedRoom, calculateStaticValues]);
  
  /**
   * Adds a new room at the specified position
   * @param {number} floor - Floor index
   * @param {number} col - Column index
   */
  const addRoom = useCallback((floor, col) => {
    if (!canPlaceRoom(rooms, floor, col)) {
      message.warning('Cannot place room at this position');
      return;
    }
    
    setRooms(prevRooms => {
      const newRooms = [...prevRooms];
      
      // Ensure floor array exists
      while (newRooms.length <= floor) {
        newRooms.push([undefined, undefined, undefined]);
      }
      
      newRooms[floor] = [...(newRooms[floor] || [undefined, undefined, undefined])];
      newRooms[floor][col] = createDefaultRoom();
      
      return newRooms;
    });
    
    setSelectedRoom([floor, col]);
    
    // Recalculate static values after adding room
    setTimeout(() => calculateStaticValues(), 0);
  }, [rooms, calculateStaticValues]);
  
  /**
   * Deletes the room at the specified position
   * @param {number} floor - Floor index
   * @param {number} col - Column index
   */
  const deleteRoom = useCallback((floor, col) => {
    if (!canDeleteRoom(rooms, floor, col)) {
      message.warning('Cannot delete this room - it would destabilize the building');
      return;
    }
    
    setRooms(prevRooms => {
      const newRooms = [...prevRooms];
      newRooms[floor] = [...newRooms[floor]];
      newRooms[floor][col] = undefined;
      
      // Remove empty top floors
      while (newRooms.length > 1 && newRooms[newRooms.length - 1].every(r => !r)) {
        newRooms.pop();
      }
      
      return newRooms;
    });
    
    // Select another room
    const remaining = rooms.flat().filter(Boolean);
    if (remaining.length > 1) {
      for (let i = 0; i < rooms.length; i++) {
        for (let j = 0; j < (rooms[i]?.length || 0); j++) {
          if (rooms[i][j] && (i !== floor || j !== col)) {
            setSelectedRoom([i, j]);
            break;
          }
        }
      }
    }
    
    // Recalculate static values after deletion
    setTimeout(() => calculateStaticValues(), 0);
  }, [rooms, calculateStaticValues]);

  // -------------------------------------------------------------------------
  // System Parameter Setters with Validation
  // -------------------------------------------------------------------------
  
  const setTOutdoorSafe = useCallback((value) => {
    const clamped = Math.max(OUTDOOR_CONFIG.MIN_OUTDOOR_TEMP, 
                              Math.min(value, OUTDOOR_CONFIG.MAX_OUTDOOR_TEMP));
    setTOutdoor(clamped);
  }, []);
  
  const setTSupplySafe = useCallback((value) => {
    const clamped = Math.max(HEAT_PUMP_CONFIG.MIN_SUPPLY_TEMP, 
                              Math.min(value, HEAT_PUMP_CONFIG.MAX_SUPPLY_TEMP));
    setTSupply(clamped);
  }, []);
  
  const setSystemPressureSafe = useCallback((value) => {
    const clamped = Math.max(PRESSURE_CONFIG.MIN_DP, 
                              Math.min(value, PRESSURE_CONFIG.MAX_DP));
    setSystemPressure(clamped);
  }, []);

  // -------------------------------------------------------------------------
  // Return State and Functions
  // -------------------------------------------------------------------------
  
  return {
    // System parameters
    tOutdoor,
    setTOutdoor: setTOutdoorSafe,
    tSupply,
    setTSupply: setTSupplySafe,
    uRoomToRoom,
    setURoomToRoom,
    systemPressure,
    setSystemPressure: setSystemPressureSafe,
    kvMax,
    setKvMax,
    kRad,
    setKRad,
    pipeLength,
    setPipeLength,
    pipeDiameter,
    setPipeDiameter,
    airChangeRate,
    setAirChangeRate,
    
    // Building state
    rooms,
    setRooms,
    selectedRoom,
    setSelectedRoom,
    
    // Computed values (from calculateStaticValues)
    totalEnvelopeLoss,
    totalVentilationLoss,
    totalHeatOutput,
    totalMassFlow,
    totalMassFlowLPM: massFlowToLPM(totalMassFlow),
    
    // Simulation control
    isRunning,
    setIsRunning,
    runSimulationStep,
    calculateStaticValues,
    
    // Derived values
    cop,
    getMixedReturnTemp,
    getCombinedValveOpening,
    
    // Room manipulation
    updateSelectedRoom,
    addRoom,
    deleteRoom,
  };
};

// #endregion SIMULATION STATE HOOK

// #region VISUALIZATION STATE HOOK
// ============================================================================
// VISUALIZATION STATE HOOK
// ============================================================================

/**
 * Hook for managing visualization overlay states
 * 
 * @returns {Object} Visualization state and toggle functions
 */
export const useVisualizationOverlays = () => {
  /** Show radiator output rating overlay */
  const [showRadiatorRating, setShowRadiatorRating] = useState(false);
  
  /** Show room temperature rating overlay */
  const [showTemperatureRating, setShowTemperatureRating] = useState(false);
  
  /** Show heat flux arrows overlay */
  const [showHeatFlux, setShowHeatFlux] = useState(false);
  
  /** Show U-value rating overlay */
  const [showUValueRating, setShowUValueRating] = useState(false);
  
  /**
   * Toggles a visualization overlay (only one active at a time)
   * @param {string} overlay - Overlay name
   */
  const toggleOverlay = useCallback((overlay) => {
    switch (overlay) {
      case 'radiator':
        setShowRadiatorRating(prev => !prev);
        setShowTemperatureRating(false);
        setShowHeatFlux(false);
        setShowUValueRating(false);
        break;
      case 'temperature':
        setShowRadiatorRating(false);
        setShowTemperatureRating(prev => !prev);
        setShowHeatFlux(false);
        setShowUValueRating(false);
        break;
      case 'heatFlux':
        setShowRadiatorRating(false);
        setShowTemperatureRating(false);
        setShowHeatFlux(prev => !prev);
        setShowUValueRating(false);
        break;
      case 'uValue':
        setShowRadiatorRating(false);
        setShowTemperatureRating(false);
        setShowHeatFlux(false);
        setShowUValueRating(prev => !prev);
        break;
      default:
        break;
    }
  }, []);
  
  return {
    showRadiatorRating,
    showTemperatureRating,
    showHeatFlux,
    showUValueRating,
    toggleOverlay,
  };
};

// #endregion VISUALIZATION STATE HOOK

// #region IMPORT/EXPORT HOOK
// ============================================================================
// IMPORT/EXPORT HOOK
// ============================================================================

/**
 * Hook for handling configuration import and export
 * 
 * @param {Object} simulation - Simulation state from useSimulation
 * @returns {Object} Import/export functions
 */
export const useConfigurationIO = (simulation) => {
  const [isLoading, setIsLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  
  const {
    tOutdoor, tSupply, uRoomToRoom, systemPressure, kvMax, kRad, pipeLength, pipeDiameter, airChangeRate,
    rooms, setRooms,
    setTOutdoor, setTSupply, setURoomToRoom, setSystemPressure, setKvMax, setKRad, setPipeLength, setPipeDiameter, setAirChangeRate,
  } = simulation;
  
  /**
   * Exports current configuration to a JSON file
   */
  const exportConfiguration = useCallback(() => {
    const config = {
      version: '2.0.0',
      timestamp: new Date().toISOString(),
      systemParameters: {
        tOutdoor,
        tSupply,
        uRoomToRoom,
        systemPressure,
        kvMax,
        kRad,
        pipeLength,
        pipeDiameter,
        airChangeRate,
      },
      building: rooms.map(floor => 
        floor.map(room => room ? {
          nRad: room.nRad,
          Treq: room.Treq,
          valve: room.valve,
          Uout: room.Uout,
        } : null)
      ),
    };
    
    const blob = new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `hydraulic-balancing-config-${Date.now()}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
    messageApi.success('Configuration exported successfully');
  }, [tOutdoor, tSupply, uRoomToRoom, systemPressure, kvMax, kRad, pipeLength, pipeDiameter, airChangeRate, rooms, messageApi]);
  
  /**
   * Imports configuration from a file
   * @param {Object} info - Ant Design Upload file info
   */
  const importConfiguration = useCallback(async (info) => {
    const { file } = info;
    
    if (file.status === 'uploading') {
      setIsLoading(true);
      return;
    }
    
    if (file.status === 'done' || file.originFileObj) {
      setIsLoading(true);
      
      try {
        const text = await file.originFileObj.text();
        const config = JSON.parse(text);
        
        // Validate version
        if (!config.version || !config.version.startsWith('2.')) {
          throw new Error('Incompatible configuration version');
        }
        
        // Apply system parameters
        const { systemParameters } = config;
        if (systemParameters) {
          setTOutdoor(systemParameters.tOutdoor ?? OUTDOOR_CONFIG.DEFAULT_OUTDOOR_TEMP);
          setTSupply(systemParameters.tSupply ?? HEAT_PUMP_CONFIG.DEFAULT_SUPPLY_TEMP);
          setURoomToRoom(systemParameters.uRoomToRoom ?? BUILDING_CONFIG.DEFAULT_U_ROOM_TO_ROOM);
          setSystemPressure(systemParameters.systemPressure ?? PRESSURE_CONFIG.DEFAULT_DP);
          setKvMax(systemParameters.kvMax ?? VALVE_CONFIG.DEFAULT_KV_MAX);
          setKRad(systemParameters.kRad ?? RADIATOR_CONFIG.DEFAULT_K_RAD);
          setPipeLength(systemParameters.pipeLength ?? PIPING_CONFIG.DEFAULT_PIPE_LENGTH);
          setPipeDiameter(systemParameters.pipeDiameter ?? PIPING_CONFIG.DEFAULT_PIPE_DIAMETER);
          setAirChangeRate(systemParameters.airChangeRate ?? 0.5);
        }
        
        // Apply building configuration
        if (config.building) {
          const newRooms = config.building.map(floor =>
            floor.map(room => room ? createDefaultRoom(room) : undefined)
          );
          setRooms(newRooms);
        }
        
        messageApi.success('Configuration imported successfully');
      } catch (error) {
        console.error('Import error:', error);
        messageApi.error(`Failed to import configuration: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    }
  }, [setRooms, setTOutdoor, setTSupply, setURoomToRoom, setSystemPressure, setKvMax, setKRad, setPipeLength, setPipeDiameter, setAirChangeRate, messageApi]);
  
  return {
    isLoading,
    exportConfiguration,
    importConfiguration,
    contextHolder,
  };
};

// #endregion IMPORT/EXPORT HOOK

// #region ROOM SELECTION HOOK
// ============================================================================
// ROOM SELECTION HOOK
// ============================================================================

/**
 * Hook for room selection and navigation
 * 
 * @param {Array<Array<Object>>} rooms - Building grid
 * @param {Array<number>} selectedRoom - Current selection [floor, col]
 * @param {Function} setSelectedRoom - Selection setter
 * @returns {Object} Selection utilities
 */
export const useRoomSelection = (rooms, selectedRoom, setSelectedRoom) => {
  /**
   * Gets list of all valid room positions
   */
  const getValidRoomPositions = useCallback(() => {
    const positions = [];
    
    rooms.forEach((floor, floorIndex) => {
      floor.forEach((room, colIndex) => {
        if (room) {
          positions.push({
            floor: floorIndex,
            col: colIndex,
            roomNumber: floorIndex * BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR + colIndex + 1,
          });
        }
      });
    });
    
    return positions;
  }, [rooms]);
  
  /**
   * Gets the currently selected room object
   */
  const getSelectedRoom = useCallback(() => {
    const [floor, col] = selectedRoom;
    return rooms[floor]?.[col] || null;
  }, [rooms, selectedRoom]);
  
  /**
   * Converts room position to room number (1-indexed)
   */
  const positionToRoomNumber = useCallback((floor, col) => {
    return floor * BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR + col + 1;
  }, []);
  
  /**
   * Selects room by room number
   */
  const selectByRoomNumber = useCallback((roomNumber) => {
    const floor = Math.floor((roomNumber - 1) / BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR);
    const col = (roomNumber - 1) % BUILDING_CONFIG.MAX_ROOMS_PER_FLOOR;
    
    if (rooms[floor]?.[col]) {
      setSelectedRoom([floor, col]);
    }
  }, [rooms, setSelectedRoom]);
  
  return {
    getValidRoomPositions,
    getSelectedRoom,
    positionToRoomNumber,
    selectByRoomNumber,
    selectedFloor: selectedRoom[0],
    selectedCol: selectedRoom[1],
    selectedRoomNumber: positionToRoomNumber(selectedRoom[0], selectedRoom[1]),
  };
};

// #endregion ROOM SELECTION HOOK
